<script setup lang="ts">
  import { IconRadio, PositionUserSelector } from '@/shared/ui';
  import TrainingSessionItem from './TrainingSessionItem.vue';
  import type { ApiResponse } from '@/shared/lib';
  import type { UserItem } from '@/shared/lib';
  import type { Position } from '@/entities/Position';
  import type { EditHomologationFormData } from '../../model/editHomologation.types';

  interface UserOption {
    id: number;
    name: string;
  }

  interface SessionData {
    date: Date | null;
  }

  const props = defineProps<{
    hasTrainingString: string;
    hasTraining: boolean;
    sessions: SessionData[];
    canAddMore: boolean;
    positions: ApiResponse<Position[]>;
    users: ApiResponse<UserItem[]>;
    userOptions: UserOption[];
    formData: EditHomologationFormData;
    getUserPositions: (memberId: number | string) => UserOption[];
    formatDate: (dateString: string | undefined) => string;
    setHasTraining: (value: string) => void;
    getSessionUserId: (index: number) => number | string;
    setSessionUserId: (index: number, value: number | string) => void;
    getSessionPositionId: (index: number) => number | string;
    setSessionPositionId: (index: number, value: number | string) => void;
    getSessionDate: (index: number) => Date | null;
    setSessionDate: (index: number, date: Date | null) => void;
    getSessionDatePickerOpen: (index: number) => boolean;
    setSessionDatePickerOpen: (index: number, open: boolean) => void;
    addSession: () => void;
    removeSession: (index: number) => void;
  }>();

  const selectedPositionsModel = defineModel<string[]>('selectedPositions', { required: true });
  const selectedUsersModel = defineModel<string[]>('selectedUsers', { required: true });
</script>

<template>
  <div class="flex flex-col gap-3 p-6">
    <h3 class="text-sm font-semibold text-gray-07">
      Será necessário treinamento
    </h3>
    <div class="flex gap-6">
      <IconRadio
        :model-value="hasTrainingString"
        value="true"
        label="Sim"
        name="hasTraining"
        @update:model-value="setHasTraining"
      />
      <IconRadio
        :model-value="hasTrainingString"
        value="false"
        label="Não"
        name="hasTraining"
        @update:model-value="setHasTraining"
      />
    </div>

    <template v-if="hasTraining">
      <div class="mt-4 flex flex-col gap-4">
        <h4 class="text-sm font-medium text-gray-07">
          Selecione os colaboradores que deverão passar pelo treinamento
        </h4>
        <PositionUserSelector
          user-selection-id-type="userId"
          :positions="positions"
          :users="users"
          :selected-positions="selectedPositionsModel"
          :selected-users="selectedUsersModel"
          :positions-selected-count="selectedPositionsModel.length"
          :users-selected-count="selectedUsersModel.length"
          @update:selected-positions="selectedPositionsModel = $event"
          @update:selected-users="selectedUsersModel = $event"
        />
      </div>

      <div class="mt-4 flex flex-col gap-6">
        <h4 class="text-sm font-medium text-gray-07">
          Ministrante
        </h4>
        <TrainingSessionItem
          v-for="(session, index) in sessions"
          :key="index"
          :index="index"
          :state="{
            memberId: getSessionUserId(index),
            positionId: getSessionPositionId(index),
            date: getSessionDate(index),
            dateString: formData.trainingSessionUpdates?.[index]?.trainingDate,
            datePickerOpen: getSessionDatePickerOpen(index),
          }"
          :actions="{
            setMemberId: setSessionUserId,
            setPositionId: setSessionPositionId,
            setDate: setSessionDate,
            setDatePickerOpen: setSessionDatePickerOpen,
            removeSession: removeSession,
            addSession: addSession,
          }"
          :helpers="{
            userOptions: userOptions,
            getUserPositions: getUserPositions,
            formatDate: formatDate,
          }"
          :is-first="index === 0"
          :is-add-disabled="!canAddMore"
        />
      </div>
    </template>
  </div>
</template>
